/**
 * EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V2
 * 
 * Single-Page Crew Onboarding Wizard
 * 
 * Flow:
 * - Step 0: Validate Token (auto)
 * - Step 1: Set Password
 * - Step 2: Complete Profile
 * - Step 3: Upload Documents
 * - Step 4: Success/Complete
 * 
 * RULES:
 * - Single route: /crew/onboard?token=...
 * - Token must be validated first
 * - Token persisted across steps
 * - Uses existing backend endpoints
 */

import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  Lock,
  Mail,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  User,
  FileText,
  Shield,
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  Building2,
  RefreshCw,
  Send,
  Phone,
  Upload,
  Trash2,
  File,
  X,
} from "lucide-react";
import { toast } from "sonner";

// Centralized endpoints
import { CREW_ENDPOINTS } from "../api/endpoints";
// EVENTSFLO-CREW-ONBOARDING-I18N-V1
import { useI18n } from "../i18n/I18nProvider";
import { LanguageSelector } from "../i18n/LanguageSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step definitions
const STEPS = {
  VALIDATING: 0,
  PASSWORD: 1,
  PROFILE: 2,
  DOCUMENTS: 3,
  COMPLETE: 4,
};

// EVENTSFLO-CREW-ONBOARDING-I18N-V1
// Note: step labels are intentionally NOT a module-level constant any
// more — they need to react to the active language. They are resolved
// inside the component below via `t()`.

export default function CrewOnboardingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // EVENTSFLO-CREW-ONBOARDING-I18N-V1
  const { t } = useI18n();
  const stepKey = (n) => ["validate", "password", "profile", "documents", "complete"][n];
  const stepLabel = (n) =>
    t(`crewPortal.onboardingWizard.stepLabels.${stepKey(n)}`);
  
  // Token from URL
  const token = searchParams.get("token");
  
  // Core state
  const [step, setStep] = useState(STEPS.VALIDATING);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  // Candidate info (from token validation)
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [authToken, setAuthToken] = useState(null); // JWT after password set
  
  // Password form state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    mobile: "",
    date_of_birth: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "",
  });
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  
  // Recovery flow state
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [requestingNewLink, setRequestingNewLink] = useState(false);
  const [newLinkRequested, setNewLinkRequested] = useState(false);

  // =============================================================================
  // STEP 0: Validate Token
  // =============================================================================
  const validateToken = useCallback(async () => {
    if (!token) {
      setError(t("crewPortal.onboardingWizard.errors.noToken"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Use XMLHttpRequest to avoid platform script intercepting fetch
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${API_URL}${CREW_ENDPOINTS.validateToken(token)}`);
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data: json });
          } catch (e) {
            resolve({ status: xhr.status, data: { detail: xhr.responseText || "Parse error" } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send();
      });

      if (data.status >= 200 && data.status < 300) {
        const info = data.data;
        setCandidateInfo(info);
        setRequiredDocuments(info.required_documents || []);
        
        // Pre-fill profile data
        setProfileData(prev => ({
          ...prev,
          first_name: info.first_name || "",
          last_name: info.last_name || "",
        }));

        // Check if password already set
        if (!info.requires_password_setup) {
          toast.info(t("crewPortal.onboardingWizard.toast.alreadySetup"));
          navigate("/crew");
          return;
        }

        // Move to password step
        setStep(STEPS.PASSWORD);
      } else {
        // Handle error response
        const errorDetail = data.data?.detail;

        if (typeof errorDetail === "object" && errorDetail !== null) {
          setError(errorDetail.message || t("crewPortal.onboardingWizard.errors.invalidOrExpired"));
          if (errorDetail.email) {
            setRecoveryEmail(errorDetail.email);
          }
        } else {
          setError(typeof errorDetail === "string" ? errorDetail : t("crewPortal.onboardingWizard.errors.invalidOrExpired"));
        }
      }
    } catch (err) {
      console.error("Token validation error:", err);
      setError(t("crewPortal.onboardingWizard.errors.unableValidate"));
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  // Validate on mount
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // =============================================================================
  // STEP 1: Set Password
  // =============================================================================
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError("");

    // Validate password
    if (password.length < 8) {
      setError(t("crewPortal.onboardingWizard.errors.passwordTooShort"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("crewPortal.onboardingWizard.errors.passwordsDoNotMatch"));
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}${CREW_ENDPOINTS.setPassword(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        const data = await res.json();
        
        // Store auth token for subsequent API calls
        setAuthToken(data.access_token);
        localStorage.setItem("eventsflo_auth_token", data.access_token);
        localStorage.setItem("eventsflo_auth_user", JSON.stringify(data.user));
        localStorage.setItem("eventsflo_user", JSON.stringify(data.user));

        toast.success(t("crewPortal.onboardingWizard.toast.passwordSet"));
        setStep(STEPS.PROFILE);
      } else {
        const errorData = await res.json();
        setError(errorData.detail || t("crewPortal.onboardingWizard.errors.setPasswordFailed"));
      }
    } catch (err) {
      console.error("Set password error:", err);
      setError(t("crewPortal.onboardingWizard.errors.unableSetPassword"));
    } finally {
      setSubmitting(false);
    }
  };

  // =============================================================================
  // STEP 2: Update Profile
  // =============================================================================
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError("");

    // Validate required fields
    if (!profileData.first_name.trim()) {
      setError(t("crewPortal.onboardingWizard.errors.firstNameRequired"));
      return;
    }
    if (!profileData.last_name.trim()) {
      setError(t("crewPortal.onboardingWizard.errors.lastNameRequired"));
      return;
    }
    if (!profileData.mobile.trim()) {
      setError(t("crewPortal.onboardingWizard.errors.mobileRequired"));
      return;
    }

    setSubmitting(true);

    try {
      const effectiveToken = authToken || localStorage.getItem("eventsflo_auth_token");
      
      const res = await fetch(`${API_URL}${CREW_ENDPOINTS.updateProfile}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: JSON.stringify({
          first_name: profileData.first_name.trim(),
          last_name: profileData.last_name.trim(),
          mobile: profileData.mobile.trim(),
          date_of_birth: profileData.date_of_birth || null,
          address: {
            line1: profileData.address_line1 || "",
            line2: profileData.address_line2 || "",
            city: profileData.city || "",
            postcode: profileData.postcode || "",
            country: profileData.country || "",
          },
        }),
      });

      if (res.ok) {
        toast.success(t("crewPortal.onboardingWizard.toast.profileUpdated"));
        
        // Load existing documents
        await loadDocuments();
        
        setStep(STEPS.DOCUMENTS);
      } else {
        const errorData = await res.json();
        setError(errorData.detail || t("crewPortal.onboardingWizard.errors.profileUpdateFailed"));
      }
    } catch (err) {
      console.error("Update profile error:", err);
      setError(t("crewPortal.onboardingWizard.errors.unableUpdateProfile"));
    } finally {
      setSubmitting(false);
    }
  };

  // =============================================================================
  // STEP 3: Document Management
  // =============================================================================
  const loadDocuments = async () => {
    try {
      const effectiveToken = authToken || localStorage.getItem("eventsflo_auth_token");
      
      const res = await fetch(`${API_URL}${CREW_ENDPOINTS.getDocuments}`, {
        headers: {
          Authorization: `Bearer ${effectiveToken}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error("Load documents error:", err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("crewPortal.onboardingWizard.toast.fileTooLarge"));
      return;
    }

    setUploadingDoc(true);

    try {
      const effectiveToken = authToken || localStorage.getItem("eventsflo_auth_token");
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "general");

      const res = await fetch(`${API_URL}${CREW_ENDPOINTS.uploadDocument}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: formData,
      });

      if (res.ok) {
        toast.success(t("crewPortal.onboardingWizard.toast.docUploaded"));
        await loadDocuments();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || t("crewPortal.onboardingWizard.toast.docUploadFailed"));
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(t("crewPortal.onboardingWizard.toast.docUploadFailed"));
    } finally {
      setUploadingDoc(false);
      // Reset file input
      e.target.value = "";
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      const effectiveToken = authToken || localStorage.getItem("eventsflo_auth_token");
      
      const res = await fetch(`${API_URL}${CREW_ENDPOINTS.deleteDocument(docId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${effectiveToken}`,
        },
      });

      if (res.ok) {
        toast.success(t("crewPortal.onboardingWizard.toast.docDeleted"));
        await loadDocuments();
      } else {
        toast.error(t("crewPortal.onboardingWizard.toast.docDeleteFailed"));
      }
    } catch (err) {
      console.error("Delete document error:", err);
      toast.error(t("crewPortal.onboardingWizard.toast.docDeleteFailed"));
    }
  };

  const handleCompleteOnboarding = () => {
    setStep(STEPS.COMPLETE);
  };

  // =============================================================================
  // Recovery Flow
  // =============================================================================
  const handleRequestNewLink = async (e) => {
    e.preventDefault();

    if (!recoveryEmail.trim()) {
      toast.error(t("crewPortal.onboardingWizard.toast.enterEmail"));
      return;
    }

    setRequestingNewLink(true);

    try {
      const response = await fetch(`${API_URL}/api/crew/request-new-access-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: recoveryEmail.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setNewLinkRequested(true);
        toast.success(t("crewPortal.onboardingWizard.toast.newLinkSent"));
      } else {
        toast.error(data.detail || t("crewPortal.onboardingWizard.toast.newLinkRequestFailedShort"));
      }
    } catch (err) {
      console.error("Request new link error:", err);
      toast.error(t("crewPortal.onboardingWizard.toast.newLinkRequestFailed"));
    } finally {
      setRequestingNewLink(false);
    }
  };

  // =============================================================================
  // Helper: Calculate Progress
  // =============================================================================
  const progressPercent = Math.round((step / (STEPS.COMPLETE)) * 100);

  // =============================================================================
  // RENDER: Loading State
  // =============================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">

        <div className="fixed top-3 right-3 z-50">
          <LanguageSelector variant="compact" testId="onboarding-language-selector" />
        </div>
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6 flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">{t("crewPortal.onboardingWizard.loading.validating")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =============================================================================
  // RENDER: Error State (Token Invalid/Expired)
  // =============================================================================
  if (step === STEPS.VALIDATING && error) {
    const isAlreadyUsed = error.includes("already been used") || error.includes("Please login");

    // New link requested successfully
    if (newLinkRequested) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">

        <div className="fixed top-3 right-3 z-50">
          <LanguageSelector variant="compact" testId="onboarding-language-selector" />
        </div>
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <CardTitle>{t("crewPortal.onboardingWizard.recovery.checkEmailTitle")}</CardTitle>
              <CardDescription>
                {t("crewPortal.onboardingWizard.recovery.checkEmailSubtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-emerald-500/10 border-emerald-500/50 text-emerald-400">
                <Mail className="h-4 w-4" />
                <AlertDescription>
                  {t("crewPortal.onboardingWizard.recovery.sentTo")} <strong>{recoveryEmail}</strong>. 
                  {t("crewPortal.onboardingWizard.recovery.checkInboxSpam")}
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground text-center">
                {t("crewPortal.onboardingWizard.recovery.linkExpiresIn48h")}
              </p>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                {t("crewPortal.onboardingWizard.recovery.goHome")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">

        <div className="fixed top-3 right-3 z-50">
          <LanguageSelector variant="compact" testId="onboarding-language-selector" />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className={`h-12 w-12 mx-auto mb-4 ${isAlreadyUsed ? "text-blue-500" : "text-amber-500"}`} />
            <CardTitle>{isAlreadyUsed ? t("crewPortal.onboardingWizard.recovery.accountAlreadySetUp") : t("crewPortal.onboardingWizard.recovery.linkExpiredOrInvalid")}</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAlreadyUsed ? (
              <Alert className="bg-blue-500/10 border-blue-500/50 text-blue-400">
                <AlertDescription>
                  {t("crewPortal.onboardingWizard.recovery.alreadyCompletedBody")}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert className="bg-amber-500/10 border-amber-500/50 text-amber-400">
                  <RefreshCw className="h-4 w-4" />
                  <AlertDescription>
                    {t("crewPortal.onboardingWizard.recovery.dontWorryBody")}
                  </AlertDescription>
                </Alert>

                <form onSubmit={handleRequestNewLink} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="recovery-email">{t("crewPortal.onboardingWizard.recovery.emailLabel")}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="recovery-email"
                        type="email"
                        placeholder={t("crewPortal.onboardingWizard.recovery.emailPlaceholder")}
                        value={recoveryEmail}
                        onChange={(e) => setRecoveryEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="recovery-email-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={requestingNewLink} data-testid="request-new-link-btn">
                    {requestingNewLink ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("crewPortal.onboardingWizard.recovery.sending")}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {t("crewPortal.onboardingWizard.recovery.requestNewLink")}
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            {isAlreadyUsed ? (
              <Button onClick={() => navigate("/login")} data-testid="go-to-login-btn">
                {t("crewPortal.onboardingWizard.recovery.goLogin")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/")}>
                {t("crewPortal.onboardingWizard.recovery.goHome")}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    );
  }

  // =============================================================================
  // RENDER: Step 4 - Complete
  // =============================================================================
  if (step === STEPS.COMPLETE) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center p-4">

        <div className="fixed top-3 right-3 z-50">
          <LanguageSelector variant="compact" testId="onboarding-language-selector" />
        </div>
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-emerald-400" />
            </div>
            <CardTitle className="text-2xl">{t("crewPortal.onboardingWizard.complete.title")}</CardTitle>
            <CardDescription>
              {t("crewPortal.onboardingWizard.complete.subtitle")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <Alert className="bg-emerald-500/10 border-emerald-500/30">
              <CheckCircle className="h-4 w-4 text-emerald-400" />
              <AlertDescription className="text-emerald-400">
                {t("crewPortal.onboardingWizard.complete.thanks")}
              </AlertDescription>
            </Alert>

            <div className="p-4 bg-surface-elevated/30 rounded-lg space-y-2">
              <h4 className="font-medium text-sm">{t("crewPortal.onboardingWizard.complete.nextHeading")}</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>{t("crewPortal.onboardingWizard.complete.next1")}</li>
                <li>{t("crewPortal.onboardingWizard.complete.next2")}</li>
                <li>{t("crewPortal.onboardingWizard.complete.next3")}</li>
              </ul>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-3">
            <Button onClick={() => navigate("/crew")} className="w-full" data-testid="go-to-portal-btn">
              {t("crewPortal.onboardingWizard.complete.goPortal")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/")} className="w-full">
              {t("crewPortal.onboardingWizard.complete.returnHome")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // =============================================================================
  // RENDER: Main Wizard (Steps 1-3)
  // =============================================================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center p-4">

        <div className="fixed top-3 right-3 z-50">
          <LanguageSelector variant="compact" testId="onboarding-language-selector" />
        </div>
      <Card className="w-full max-w-lg">
        {/* Header */}
        <CardHeader className="text-center pb-2">
          {candidateInfo?.company_name && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="font-medium">{candidateInfo.company_name}</span>
            </div>
          )}
          <CardTitle className="text-2xl">{t("crewPortal.onboardingWizard.header.welcomeTitle")}</CardTitle>
          <CardDescription>{t("crewPortal.onboardingWizard.header.welcomeSubtitle")}</CardDescription>

          {/* Progress Bar */}
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("crewPortal.onboardingWizard.header.stepIndicator", { step })}</span>
              <span>{progressPercent}% Complete</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between mt-4">
            {[STEPS.PASSWORD, STEPS.PROFILE, STEPS.DOCUMENTS, STEPS.COMPLETE].map((s, idx) => (
              <div key={s} className="flex flex-col items-center">
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    step >= s
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step > s ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                </div>
                <span className={`text-xs mt-1 ${step >= s ? "text-foreground" : "text-muted-foreground"}`}>
                  {stepLabel(idx + 1)}
                </span>
              </div>
            ))}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* =================================================================== */}
          {/* STEP 1: Password */}
          {/* =================================================================== */}
          {step === STEPS.PASSWORD && (
            <form onSubmit={handleSetPassword} className="space-y-4" data-testid="password-step">
              {/* Email Display */}
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium text-emerald-400">{t("crewPortal.onboardingWizard.password.emailRecognized")}</p>
                    <p className="text-sm text-muted-foreground">{candidateInfo?.email}</p>
                  </div>
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password">{t("crewPortal.onboardingWizard.password.create")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("crewPortal.onboardingWizard.password.newPasswordPlaceholder")}
                    className="pl-10 pr-10"
                    required
                    minLength={8}
                    data-testid="onboarding-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">{t("crewPortal.onboardingWizard.password.minHint")}</p>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("crewPortal.onboardingWizard.password.confirmLabel")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("crewPortal.onboardingWizard.password.confirmPlaceholder")}
                    className="pl-10 pr-10"
                    required
                    data-testid="onboarding-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={submitting} data-testid="set-password-btn">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("crewPortal.onboardingWizard.password.settingUp")}
                  </>
                ) : (
                  <>
                    {t("crewPortal.onboardingWizard.password.continueProfile")}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* =================================================================== */}
          {/* STEP 2: Profile */}
          {/* =================================================================== */}
          {step === STEPS.PROFILE && (
            <form onSubmit={handleUpdateProfile} className="space-y-4" data-testid="profile-step">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">
                    {t("crewPortal.onboardingWizard.profile.firstName")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="first_name"
                    value={profileData.first_name}
                    onChange={(e) =>
                      setProfileData((prev) => ({ ...prev, first_name: e.target.value }))
                    }
                    placeholder={t("crewPortal.onboardingWizard.profile.firstNamePh")}
                    required
                    data-testid="profile-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">
                    {t("crewPortal.onboardingWizard.profile.lastName")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="last_name"
                    value={profileData.last_name}
                    onChange={(e) =>
                      setProfileData((prev) => ({ ...prev, last_name: e.target.value }))
                    }
                    placeholder={t("crewPortal.onboardingWizard.profile.lastNamePh")}
                    required
                    data-testid="profile-last-name"
                  />
                </div>
              </div>

              {/* Mobile */}
              <div className="space-y-2">
                <Label htmlFor="mobile">
                  {t("crewPortal.onboardingWizard.profile.mobile")} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="mobile"
                    type="tel"
                    value={profileData.mobile}
                    onChange={(e) =>
                      setProfileData((prev) => ({ ...prev, mobile: e.target.value }))
                    }
                    placeholder="+33 6 12 34 56 78"
                    className="pl-10"
                    required
                    data-testid="profile-mobile"
                  />
                </div>
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <Label htmlFor="dob">{t("crewPortal.onboardingWizard.profile.dob")}</Label>
                <Input
                  id="dob"
                  type="date"
                  value={profileData.date_of_birth}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, date_of_birth: e.target.value }))
                  }
                  data-testid="profile-dob"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label>{t("crewPortal.onboardingWizard.profile.address")}</Label>
                <Input
                  placeholder={t("crewPortal.onboardingWizard.profile.addrLine1Ph")}
                  value={profileData.address_line1}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, address_line1: e.target.value }))
                  }
                  className="mb-2"
                  data-testid="profile-address1"
                />
                <Input
                  placeholder={t("crewPortal.onboardingWizard.profile.addrLine2Ph")}
                  value={profileData.address_line2}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, address_line2: e.target.value }))
                  }
                  data-testid="profile-address2"
                />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input
                    placeholder={t("crewPortal.onboardingWizard.profile.cityPh")}
                    value={profileData.city}
                    onChange={(e) =>
                      setProfileData((prev) => ({ ...prev, city: e.target.value }))
                    }
                    data-testid="profile-city"
                  />
                  <Input
                    placeholder={t("crewPortal.onboardingWizard.profile.postcodePh")}
                    value={profileData.postcode}
                    onChange={(e) =>
                      setProfileData((prev) => ({ ...prev, postcode: e.target.value }))
                    }
                    data-testid="profile-postcode"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(STEPS.PASSWORD)}
                  className="flex-1"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("crewPortal.onboardingWizard.profile.back")}
                </Button>
                <Button type="submit" className="flex-1" disabled={submitting} data-testid="profile-submit-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("crewPortal.onboardingWizard.profile.saving")}
                    </>
                  ) : (
                    <>
                      {t("crewPortal.onboardingWizard.profile.continueDocs")}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* =================================================================== */}
          {/* STEP 3: Documents */}
          {/* =================================================================== */}
          {step === STEPS.DOCUMENTS && (
            <div className="space-y-4" data-testid="documents-step">
              {/* Required Documents Notice */}
              {requiredDocuments.length > 0 && (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertDescription className="text-amber-400">
                    <strong>{t("crewPortal.onboardingWizard.documents.requiredHeading")}</strong>
                    <ul className="list-disc list-inside mt-1 text-sm">
                      {requiredDocuments.map((doc, idx) => (
                        <li key={idx}>
                          {doc.document_type_name}
                          {doc.is_mandatory && <span className="text-red-400 ml-1">*</span>}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Upload Area */}
              <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                  disabled={uploadingDoc}
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer flex flex-col items-center gap-2 ${
                    uploadingDoc ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {uploadingDoc ? (
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium">
                    {uploadingDoc ? t("crewPortal.onboardingWizard.documents.uploading") : t("crewPortal.onboardingWizard.documents.clickUpload")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("crewPortal.onboardingWizard.documents.uploadHint")}
                  </span>
                </label>
              </div>

              {/* Uploaded Documents List */}
              {documents.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm">{t("crewPortal.onboardingWizard.documents.uploadedHeading")}</Label>
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-surface-elevated/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <File className="h-5 w-5 text-primary" />
                          <div>
                            <p className="text-sm font-medium truncate max-w-[200px]">
                              {doc.original_filename || doc.name || "Document"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {doc.document_type || "General"}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {documents.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t("crewPortal.onboardingWizard.documents.emptyState")}
                </p>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(STEPS.PROFILE)}
                  className="flex-1"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("crewPortal.onboardingWizard.profile.back")}
                </Button>
                <Button
                  onClick={handleCompleteOnboarding}
                  className="flex-1"
                  data-testid="complete-onboarding-btn"
                >
                  {t("crewPortal.onboardingWizard.documents.completeBtn")}
                  <CheckCircle className="h-4 w-4 ml-2" />
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                {t("crewPortal.onboardingWizard.documents.uploadMoreLaterHint")}
              </p>
            </div>
          )}
        </CardContent>

        {/* Footer */}
        <CardFooter className="flex flex-col gap-2 text-center text-xs text-muted-foreground border-t pt-4">
          <p>
            {t("crewPortal.onboardingWizard.footer.activationNote")}
          </p>
          <p>
            {t("crewPortal.onboardingWizard.footer.alreadyHaveAccount")}{" "}
            <a href="/login" className="text-primary hover:underline">
              {t("crewPortal.onboardingWizard.footer.loginHere")}
            </a>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
